<script setup lang="ts">
// 此组件指定位置后，位置不会自动调整
import { ref, nextTick, type CSSProperties, onUnmounted } from 'vue'
import { onClickOutside, onKeyStroke, useDebounceFn } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    placement?: 'top' | 'bottom' | 'left' | 'right'
    gap?: number // 触发器和浮层之间的间距
    viewportMargin?: number | { top: number; bottom: number } // 顶部底部距离视窗的安全距离
    rootClass?: string // 根节点class 用来在调用时注入css var
    showArrow?: boolean // 是否显示箭头
    deviation?: number // top/bottom 水平偏移量
    zIndex?: number // 浮层层级
    closeOnHoverLeave?: boolean // 鼠标同时离开触发器和浮层时是否关闭
    clickOutsideIgnore?: Array<string | HTMLElement> // 点击外部关闭时需要忽略的元素
  }>(),
  {
    placement: 'right',
    gap: 10,
    viewportMargin: 80,
    deviation: 0,
    showArrow: true,
    zIndex: 1001,
    closeOnHoverLeave: true,
    clickOutsideIgnore: () => []
  }
)

// ===== 常量与状态 =====
const ARROW_PAD = 8
// hover debounce time
const HOVER_EXIT_DELAY_MS = 320
const isOverTrigger = ref(false)
const isOverPopup = ref(false)

const visible = ref(false)
const popoverRef = ref<HTMLElement | null>(null)
const triggerEl = ref<HTMLElement | null>(null)

const popoverStyle = ref<CSSProperties>({
  position: 'absolute',
  top: '0px',
  left: '0px',
  zIndex: props.zIndex
})

// 触发器端临时事件处理器
let offTriggerEnter: (() => void) | null = null
let offTriggerLeave: (() => void) | null = null

const emit = defineEmits<{ (e: 'after-hide'): void }>()

// 暴露 Show / Hide 方法
defineExpose({
  show,
  hideNow
})
// ====== resize 重定位 rAF 合批 ======
let offWindowResize: (() => void) | null = null
let rafId = 0

function attachWindowResizeReposition() {
  if (offWindowResize) return
  const onResize = () => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => {
      rafId = 0
      reposition()
    })
  }
  window.addEventListener('resize', onResize, { passive: true })
  offWindowResize = () => {
    window.removeEventListener('resize', onResize)
    if (rafId) cancelAnimationFrame(rafId)
    rafId = 0
  }
}

function cleanupWindowResizeReposition() {
  offWindowResize?.()
  offWindowResize = null
}

// 读 -> 算 -> 写
function layoutOnce(trigger: HTMLElement, pop: HTMLElement) {
  const metrics = readMetrics(trigger, pop)
  const bounds = computeBounds(metrics, props.viewportMargin)
  const contentMaxHeight = computeEffectiveHeight(metrics, bounds)
  const initialPos = computeInitialPosition(metrics, contentMaxHeight, props)
  const finalPos = clampPosition(initialPos, contentMaxHeight, metrics, bounds, props)
  const arrow = computeArrowCenters(finalPos, metrics, contentMaxHeight, props)
  applyStyles(popoverStyle, pop, finalPos, contentMaxHeight, arrow)

  // 左右布局 debug断言
  if (props.placement === 'left' || props.placement === 'right') {
    console.assert(
      finalPos.top <= metrics.triggerCenterY &&
        metrics.triggerCenterY <= finalPos.top + contentMaxHeight,
      '中心点未被包含'
    )
  }
}
async function show(source: MouseEvent | HTMLElement) {
  // 进入显示时同步“此刻已在触发器上”，避免防抖误关
  isOverTrigger.value = true

  // 1) 解析触发器 & 绑定 hover 保活
  const trigger = getTriggerFrom(source)
  if (!trigger) return

  triggerEl.value = trigger
  if (props.closeOnHoverLeave) {
    bindTriggerHover(trigger)
  }

  // 2) 显示并等待渲染
  visible.value = true
  await nextTick()
  if (!popoverRef.value) return

  // 3) 定位
  layoutOnce(trigger, popoverRef.value)

  // 4) 仅在可见期间绑定 window.resize → rAF 重定位
  attachWindowResizeReposition()
}

// 统一重定位（供 resize 触发；与 show 的定位流程一致 为了支持鼠标移出后不隐藏Popover的场景）
function reposition() {
  if (!visible.value || !triggerEl.value || !popoverRef.value) return
  // 菜单重渲染或布局卸载后锚点可能已脱离文档，此时定位坐标无效。
  if (!triggerEl.value.isConnected) {
    hideNow()
    return
  }
  layoutOnce(triggerEl.value, popoverRef.value)
}

/** ========== utils ========== */

// 解析触发器。HTMLElement 路径用于非点击入口复用固定锚点，不伪造 MouseEvent。
function getTriggerFrom(source: MouseEvent | HTMLElement): HTMLElement | null {
  if (source instanceof HTMLElement) return source
  return (source.currentTarget as HTMLElement) || (source.target as HTMLElement) || null
}

// 读度量（只读）
function readMetrics(trigger: HTMLElement, popover: HTMLElement) {
  const triggerRect = trigger.getBoundingClientRect() // viewport 坐标
  const scrollY = window.scrollY
  const scrollX = window.scrollX
  const viewportH = window.innerHeight
  const viewportW = window.innerWidth
  const popRect = popover.getBoundingClientRect() // 仅用 width
  const popoverWidth = popRect.width
  // 使用 scrollHeight，避免受 max-height 影响； +2 容错边框/阴影补偿
  const contentNaturalHeight = popover.scrollHeight + 2
  const triggerCenterY = scrollY + triggerRect.top + triggerRect.height / 2
  const triggerCenterX = scrollX + triggerRect.left + triggerRect.width / 2

  return {
    triggerRect,
    triggerCenterY,
    triggerCenterX,
    scrollX,
    scrollY,
    viewportW,
    viewportH,
    popoverWidth,
    contentNaturalHeight
  }
}

// 统一参数 margin，并算出可用边界
function computeBounds(
  m: ReturnType<typeof readMetrics>,
  vm: number | { top: number; bottom: number }
) {
  const marginTop = typeof vm === 'number' ? vm : vm.top
  const marginBottom = typeof vm === 'number' ? vm : vm.bottom
  // 左右复用最小上下边距 的策略 { top: 120, bottom: 60 } → 左右边距用 60。
  const marginX = typeof vm === 'number' ? vm : Math.min(marginTop || 0, marginBottom || 0)

  const availTop = m.scrollY + marginTop
  const availBottom = m.scrollY + m.viewportH - marginBottom
  const availLeft = m.scrollX + marginX
  const availRight = m.scrollX + m.viewportW - marginX
  const viewportSpanH = Math.max(0, availBottom - availTop)

  return {
    margin: { top: marginTop, right: marginX, bottom: marginBottom, left: marginX },
    availTop,
    availBottom,
    availLeft,
    availRight,
    viewportSpanH
  }
}

// 有效高度
function computeEffectiveHeight(
  m: ReturnType<typeof readMetrics>,
  b: ReturnType<typeof computeBounds>
) {
  return Math.min(m.contentNaturalHeight, b.viewportSpanH)
}

// 初始（未钳制）位置
function computeInitialPosition(m: ReturnType<typeof readMetrics>, H: number, p: typeof props) {
  const hCenter = m.scrollX + m.triggerRect.left + m.triggerRect.width / 2 - m.popoverWidth / 2
  const vCenter = m.triggerCenterY - H / 2
  let top = 0
  let left = 0
  switch (p.placement) {
    case 'bottom':
      top = m.scrollY + m.triggerRect.bottom + p.gap
      left = hCenter + (p.deviation || 0)
      break
    case 'top':
      top = m.scrollY + m.triggerRect.top - H - p.gap
      left = hCenter + (p.deviation || 0)
      break
    case 'left':
      top = vCenter
      left = m.scrollX + m.triggerRect.left - m.popoverWidth - p.gap
      break
    case 'right':
    default:
      top = vCenter
      left = m.scrollX + m.triggerRect.right + p.gap
      break
  }
  return { top, left }
}

// 钳制（左右：中心包含 ∩ 可视；上下：水平夹取）
function clampPosition(
  pos: { top: number; left: number },
  H: number,
  m: ReturnType<typeof readMetrics>,
  b: ReturnType<typeof computeBounds>,
  p: typeof props
) {
  const loViewport = b.availTop
  const hiViewport = b.availBottom - H
  const useCenterClamp = p.placement === 'left' || p.placement === 'right'

  const loCenter = m.triggerCenterY - H
  const hiCenter = m.triggerCenterY

  const lo = useCenterClamp ? Math.max(loViewport, loCenter) : loViewport
  const hi = useCenterClamp ? Math.min(hiViewport, hiCenter) : hiViewport

  const top = Math.min(Math.max(pos.top, lo), hi)
  let left = pos.left

  if (p.placement === 'top' || p.placement === 'bottom') {
    const loLeft = b.availLeft
    const hiLeft = b.availRight - m.popoverWidth
    left = Math.min(Math.max(left, loLeft), hiLeft)
  }
  return { top, left }
}

// 箭头中心（根据 showArrow 与 placement 计算 X/Y 轴）
function computeArrowCenters(
  pos: { top: number; left: number },
  m: ReturnType<typeof readMetrics>,
  H: number,
  p: typeof props
): { x: number | null; y: number | null } {
  if (!p.showArrow) return { x: null, y: null }

  if (p.placement === 'left' || p.placement === 'right') {
    const relY = m.triggerCenterY - pos.top
    const y = Math.min(Math.max(relY, ARROW_PAD), H - ARROW_PAD)
    return { x: null, y }
  } else {
    const relX = m.triggerCenterX - pos.left
    const x = Math.min(Math.max(relX, ARROW_PAD), m.popoverWidth - ARROW_PAD)
    return { x, y: null }
  }
}

// 写样式（外层：top/left/axis vars；内容：max-height/overflow）
function applyStyles(
  styleRef: typeof popoverStyle,
  popEl: HTMLElement,
  pos: { top: number; left: number },
  H: number,
  arrow: { x: number | null; y: number | null }
) {
  const viewPortStyle: Record<string, string | number> = {
    ...styleRef.value,
    top: `${pos.top}px`,
    left: `${pos.left}px`,
    '--anchor-po-max-height': `${H}px`
  }
  // 按轴分别写入
  if (arrow.x != null) viewPortStyle['--anchor-po-arrow-center-x'] = `${arrow.x}px`
  if (arrow.y != null) viewPortStyle['--anchor-po-arrow-center-y'] = `${arrow.y}px`

  popoverStyle.value = viewPortStyle
  // 内部定高
  popEl.style.maxHeight = `${H}px`
  popEl.style.overflowY = 'var(--anchor-po-overflow-y, auto)'
}

// 隐藏的防抖
const debouncedHide = useDebounceFn(() => {
  if (props.closeOnHoverLeave && !isOverTrigger.value && !isOverPopup.value) {
    hideNow()
  }
}, HOVER_EXIT_DELAY_MS)

// 立即关闭无防抖  ***** 本地开发注释这个方法的内容就可以不关闭浮层 *****
function hideNow() {
  // 隐藏时解绑 resize
  cleanupWindowResizeReposition()
  // 解除触发器 hover 监听，避免泄漏
  unbindTriggerHover()
  triggerEl.value = null

  visible.value = false
  // 复位悬浮状态，避免残留状态影响后续判断
  isOverTrigger.value = false
  isOverPopup.value = false
  emit('after-hide')
}

/**
 * 悬浮状态更新（浮层端）
 */
function onPopupEnter() {
  isOverPopup.value = true
}
function onPopupLeave() {
  isOverPopup.value = false
  if (props.closeOnHoverLeave && !isOverTrigger.value) debouncedHide()
}

/**
 * 悬浮状态更新（触发器端）
 */
function onTriggerEnter() {
  isOverTrigger.value = true
  // 不需要取消防抖；若存在待触发的隐藏，到点会因状态已变而不执行
}
function onTriggerLeave() {
  isOverTrigger.value = false
  // 两端都不在时，启动一次防抖隐藏检查
  if (props.closeOnHoverLeave && !isOverPopup.value) debouncedHide()
}

function unbindTriggerHover() {
  offTriggerEnter?.()
  offTriggerLeave?.()
  offTriggerEnter = null
  offTriggerLeave = null
}

// 绑定/解绑触发器的 hover 监听
function bindTriggerHover(el: HTMLElement | null) {
  // 解绑旧的监听
  unbindTriggerHover()
  triggerEl.value = el
  if (!el) return

  const enter = () => onTriggerEnter()
  const leave = () => onTriggerLeave()
  el.addEventListener('mouseenter', enter)
  el.addEventListener('mouseleave', leave)
  offTriggerEnter = () => el.removeEventListener('mouseenter', enter)
  offTriggerLeave = () => el.removeEventListener('mouseleave', leave)
}

// VueUse Hook:点击外部关闭 ESC 键关闭
onClickOutside(popoverRef, () => hideNow(), {
  ignore: [triggerEl, ...props.clickOutsideIgnore]
})
onKeyStroke('Escape', (e) => {
  if (visible.value) {
    e.preventDefault()
    hideNow()
  }
})

// 清理触发器监听
onUnmounted(() => {
  cleanupWindowResizeReposition()
  unbindTriggerHover()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="anchor-po-fade">
      <div :class="['anchor-po-viewport', props.rootClass]" v-if="visible" :style="popoverStyle">
        <div
          ref="popoverRef"
          class="anchor-po"
          @mouseenter="onPopupEnter"
          @mouseleave="onPopupLeave">
          <div v-if="showArrow" :class="`arrow arrow-${props.placement}`" />
          <div class="anchor-po-content">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
/* 降权 */
:where(.anchor-po-viewport) {
  --anchor-po-bg: #3a1d66;
  --anchor-po-border-color: #ffffff1a;
  --anchor-po-border: 0.5px solid var(--anchor-po-border-color);
  --anchor-po-arrow-size: 10px;
  --anchor-po-arrow-pos: -5px;
  --anchor-po-padding: 10px;
  --anchor-po-whole-border: var(--anchor-po-border);
  --anchor-po-box-shadow: 4px 0px 4px 0px rgba(0, 0, 0, 0.02);
  --anchor-po-box-radius: 6px;

  /* 箭头中心的轴向变量（根据 placement 使用） */
  --anchor-po-arrow-center-x: 50%;
  --anchor-po-arrow-center-y: 50%;
}
.anchor-po {
  background: var(--anchor-po-bg);
  border: var(--anchor-po-whole-border);
  box-shadow: var(--anchor-po-box-shadow);
  border-radius: var(--anchor-po-box-radius);
  color: white;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.anchor-po::-webkit-scrollbar {
  display: none;
}

.anchor-po-content {
  padding: var(--anchor-po-padding);
}

.anchor-po-fade-enter-active {
  transition: opacity 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.anchor-po-fade-leave-active {
  transition: opacity 140ms cubic-bezier(0.4, 0, 1, 1);
}
.anchor-po-fade-enter-from,
.anchor-po-fade-leave-to {
  opacity: 0;
}
.anchor-po-fade-enter-to,
.anchor-po-fade-leave-from {
  opacity: 1;
}

/* 三角箭头 */
.arrow {
  position: absolute;
  width: var(--anchor-po-arrow-size);
  height: var(--anchor-po-arrow-size);
  background: var(--anchor-po-bg);
  transform: rotate(45deg);
  border: var(--anchor-po-border);
}
.arrow-right {
  left: var(--anchor-po-arrow-pos);
  top: var(--anchor-po-arrow-center-y);
  margin-top: var(--anchor-po-arrow-pos);
  border-right: none;
  border-top: none;
}
.arrow-left {
  right: var(--anchor-po-arrow-pos);
  top: var(--anchor-po-arrow-center-y);
  margin-top: var(--anchor-po-arrow-pos);
  border-left: none;
  border-bottom: none;
}
.arrow-bottom {
  top: var(--anchor-po-arrow-pos);
  left: var(--anchor-po-arrow-center-x);
  margin-left: var(--anchor-po-arrow-pos);
  border-right: none;
  border-bottom: none;
}
.arrow-top {
  bottom: var(--anchor-po-arrow-pos);
  left: var(--anchor-po-arrow-center-x);
  margin-left: var(--anchor-po-arrow-pos);
  border-left: none;
  border-top: none;
}
</style>
